import { useCallback, useEffect, useRef, useState } from 'react';

const SIDEBAR_MODE_KEY = 'crowdex.desktop.sidebar-mode';
const REMOVED_PROJECTS_KEY = 'crowdex.desktop.removed-projects';

/** The sidebar's width (plan §2.1): one 300 px bar. */
export const SIDEBAR_WIDTH = 300;
/** Its rail (D2): icons only. */
export const SIDEBAR_RAIL_WIDTH = 60;

/**
 * How the sidebar shows (D1, D2): open, collapsed to its rail, or hidden (⌘S), revealed over the
 * content while the pointer rests on the window's left edge.
 */
export type SidebarMode = 'open' | 'rail' | 'hidden';

function storedStringArray(key: string): readonly string[] {
  const stored = localStorage.getItem(key);
  if (stored === null) return [];
  try {
    const values: unknown = JSON.parse(stored);
    if (Array.isArray(values)) {
      return values.filter((value): value is string => typeof value === 'string');
    }
  } catch {
    localStorage.removeItem(key);
  }
  return [];
}

function storedStringSet(key: string): ReadonlySet<string> {
  return new Set(storedStringArray(key));
}

function storedMode(): SidebarMode {
  const stored = localStorage.getItem(SIDEBAR_MODE_KEY);
  return stored === 'rail' || stored === 'hidden' ? stored : 'open';
}

export function useDesktopSidebar() {
  const [removedProjects, setRemovedProjects] = useState<ReadonlySet<string>>(() => new Set());
  const [mode, setModeState] = useState<SidebarMode>('open');
  // The mode ⌘S shows again: the bar, or its rail.
  const shownModeRef = useRef<Exclude<SidebarMode, 'hidden'>>('open');
  const [revealed, setRevealed] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setRemovedProjects(storedStringSet(REMOVED_PROJECTS_KEY));
    setModeState(storedMode());
  }, []);

  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const setMode = useCallback((next: SidebarMode) => {
    if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    if (next !== 'hidden') shownModeRef.current = next;
    setRevealed(false);
    setModeState(next);
    try {
      localStorage.setItem(SIDEBAR_MODE_KEY, next);
    } catch {
      // Storage may be unavailable: the choice lasts for this session.
    }
  }, []);

  /** ⌘S: hides the sidebar, or shows it again as it was. */
  function toggleHidden(): void {
    setMode(mode === 'hidden' ? shownModeRef.current : 'hidden');
  }

  /** Shows the hidden sidebar over the content (the pointer on the window's left edge). */
  function reveal(): void {
    if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    if (mode === 'hidden') setRevealed(true);
  }

  function scheduleClose(): void {
    if (mode !== 'hidden') return;
    if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRevealed(false), 180);
  }

  function restoreProject(project: string): void {
    setRemovedProjects((current) => {
      if (!current.has(project)) return current;
      const next = new Set(current);
      next.delete(project);
      localStorage.setItem(REMOVED_PROJECTS_KEY, JSON.stringify([...next]));
      return next;
    });
  }

  const reconcileRemovedProjects = useCallback(
    (activeProjects: readonly string[], removedProjects: readonly string[]): void => {
      const active = new Set(activeProjects);
      setRemovedProjects((current) => {
        const next = new Set([...current].filter((project) => !active.has(project)));
        for (const project of removedProjects) {
          if (!active.has(project)) next.add(project);
        }
        localStorage.setItem(REMOVED_PROJECTS_KEY, JSON.stringify([...next]));
        return next;
      });
    },
    [],
  );
  return {
    mode,
    removedProjects,
    revealed,
    reconcileRemovedProjects,
    restoreProject,
    reveal,
    scheduleClose,
    setMode,
    toggleHidden,
  };
}
